'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import { toast } from 'sonner';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell,  } from 'recharts';

interface StatsData {
  followUps: number;
  activeLeads: number;
  siteVisits: number;
  conversions: number;
  overdueFollowUps: number;
  newLeadsThisWeek: number;
  siteVisitsThisWeek: number;
  conversionRate: string;
  totalLeads: number;
  quotationPending: number;
  notInterested: number;
}

interface RecentLead {
  id: string;
  name: string;
  company: string;
  phone: string;
  area_sqft: number;
  status: string;
  created_at: string;
}

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
}

const statusColors: Record<string, string> = {
  follow_up: '#f59e0b',
  quotation_pending: '#3b82f6',
  interested: '#10b981',
  negotiation: '#f97316',
  new: '#6366f1',
  attempted: '#6b7280',
  connected: '#8b5cf6',
  won: '#22c55e',
  lost: '#ef4444',
  site_visit_scheduled: '#06b6d4',
  site_visit_done: '#0891b2',
  marketing_assign: '#ec4899',
  not_interested: '#9ca3af',
};

const statusLabels: Record<string, string> = {
  follow_up: 'Follow-up',
  quotation_pending: 'Quotation Pending',
  interested: 'Interested',
  negotiation: 'Negotiation',
  new: 'New',
  attempted: 'Attempted',
  connected: 'Connected',
  won: 'Won',
  lost: 'Lost',
  site_visit_scheduled: 'Site Visit',
  site_visit_done: 'Visit Done',
  marketing_assign: 'Mktg Assign',
  not_interested: 'Not Interested',
};

function getTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function getStatusBadgeStyle(status: string) {
  const color = statusColors[status] || '#6b7280';
  return { color, backgroundColor: color + '18', border: `1px solid ${color}40` };
}

// Mini sparkline data (static trend for visual richness)
const sparkFollow = [3, 5, 4, 7, 6, 8, 9];
const sparkActive = [180, 195, 210, 205, 220, 235, 247];
const sparkVisit = [4, 6, 5, 8, 7, 9, 11];
const sparkConv = [2, 3, 4, 3, 5, 6, 7];

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 56;
  const h = 28;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
      <polyline
        points={pts.join(' ')}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.85"
      />
      <circle
        cx={parseFloat(pts[pts.length - 1].split(',')[0])}
        cy={parseFloat(pts[pts.length - 1].split(',')[1])}
        r="2.5"
        fill={color}
      />
    </svg>
  );
}

function CircleProgress({ pct, color, size = 44 }: { pct: number; color: string; size?: number }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#f0f0f0" strokeWidth="5" fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth="5"
        fill="none"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

export default function HomePage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState<StatsData>({
    followUps: 0, activeLeads: 0, siteVisits: 0, conversions: 0,
    overdueFollowUps: 0, newLeadsThisWeek: 0, siteVisitsThisWeek: 0,
    conversionRate: '0%', totalLeads: 0, quotationPending: 0, notInterested: 0,
  });
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([]);
  const [chartData, setChartData] = useState<ChartDataItem[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [greeting, setGreeting] = useState('Good Morning');

  useEffect(() => {
    const hour = new Date().getHours();
    setGreeting(hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening');
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      router.replace('/login');
    }
  }, [user, authLoading, router]);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    async function loadData() {
      setDataLoading(true);
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        setUserProfile(profile);

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

        const isExecutive =
          profile?.role === 'calling_executive' ||
          profile?.role === 'marketing_executive' ||
          profile?.role === 'sales_executive';

        const isCallingExecutive = profile?.role === 'calling_executive';
        const isSalesExecutive = profile?.role === 'sales_executive';

        let leadsQuery = supabase
          .from('leads')
          .select('id, status, created_at, updated_at, name, company, phone, area_sqft')
          .order('created_at', { ascending: false });

        if (isExecutive) {
          leadsQuery = leadsQuery.or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`);
        }

        const { data: ownLeads, error: leadsError } = await leadsQuery;

        if (leadsError) {
          console.error('Dashboard leads query failed:', leadsError);
          toast.error(leadsError.message || 'Could not load leads for the dashboard.');
        }

        // For calling executives, also fetch leads from mapped marketing users
        let mappedLeads: any[] = [];
        if (isCallingExecutive) {
          // Observed leads (calling_team_observer)
          const { data: observed } = await supabase
            .from('leads')
            .select('id, status, created_at, updated_at, name, company, phone, area_sqft')
            .eq('calling_team_observer', user.id)
            .order('created_at', { ascending: false });

          const ownIds = new Set((ownLeads || []).map((l: any) => l.id));
          const observedFiltered = (observed || []).filter((l: any) => !ownIds.has(l.id));

          // Mapped marketing user leads
          const { data: mappings } = await supabase
            .from('agent_marketing_mappings')
            .select('marketing_user_id')
            .eq('calling_agent_id', user.id);

          let mappingLeads: any[] = [];
          if (mappings && mappings.length > 0) {
            const marketingUserIds = mappings.map((m: any) => m.marketing_user_id);
            const orFilter = marketingUserIds
              .flatMap((id: string) => [`assigned_to.eq.${id}`, `created_by.eq.${id}`])
              .join(',');
            const { data: mLeads } = await supabase
              .from('leads')
              .select('id, status, created_at, updated_at, name, company, phone, area_sqft')
              .or(orFilter)
              .order('created_at', { ascending: false });
            const existingIds = new Set([
              ...Array.from(ownIds),
              ...observedFiltered.map((l: any) => l.id),
            ]);
            mappingLeads = (mLeads || []).filter((l: any) => !existingIds.has(l.id));
          }

          mappedLeads = [...observedFiltered, ...mappingLeads];
        }

        // For sales executives, also fetch leads assigned to them via assigned_to_sales
        if (isSalesExecutive) {
          const { data: salesAssignedLeads } = await supabase
            .from('leads')
            .select('id, status, created_at, updated_at, name, company, phone, area_sqft')
            .eq('assigned_to_sales', user.id)
            .order('created_at', { ascending: false });
          const ownIds = new Set((ownLeads || []).map((l: any) => l.id));
          const newSalesLeads = (salesAssignedLeads || []).filter((l: any) => !ownIds.has(l.id));
          mappedLeads = [...mappedLeads, ...newSalesLeads];
        }

        const leads = [...(ownLeads || []), ...mappedLeads];

        // Fetch pending follow-up schedule items
        let scheduleFollowUpsQuery = supabase
          .from('lead_schedule_items')
          .select('id', { count: 'exact', head: true })
          .eq('type', 'followup')
          .eq('status', 'pending');

        if (isExecutive) {
          scheduleFollowUpsQuery = scheduleFollowUpsQuery.eq('assigned_to', user.id);
        }

        const { count: scheduleFollowUpsCount } = await scheduleFollowUpsQuery;

        // Fetch pending site visit schedule items
        let scheduleSiteVisitsQuery = supabase
          .from('lead_schedule_items')
          .select('id', { count: 'exact', head: true })
          .eq('type', 'sitevisit')
          .eq('status', 'pending');

        if (isExecutive) {
          scheduleSiteVisitsQuery = scheduleSiteVisitsQuery.eq('assigned_to', user.id);
        }

        const { count: scheduleSiteVisitsCount } = await scheduleSiteVisitsQuery;

        // Derive active leads count from the already-fetched leads array
        // (this correctly includes mapped marketing leads for calling executives)
        const activeLeadsCount = leads.filter(l => l.status !== 'won' && l.status !== 'lost').length;

        if (leads) {
          const leadStatusFollowUps = leads.filter(l => l.status === 'follow_up').length;
          const pendingScheduleFollowUps = scheduleFollowUpsCount ?? 0;
          const followUps = leadStatusFollowUps + pendingScheduleFollowUps;
          const overdueFollowUps = leads.filter(l =>
            l.status === 'follow_up' && l.updated_at < todayStart
          ).length;
          const activeLeads = activeLeadsCount;
          const newLeadsThisWeek = leads.filter(l => l.created_at >= weekAgo).length;
          const siteVisitsFromLeads = leads.filter(l =>
            l.status === 'site_visit_scheduled' || l.status === 'site_visit_done'
          ).length;
          const siteVisits = siteVisitsFromLeads + (scheduleSiteVisitsCount ?? 0);
          const siteVisitsThisWeek = leads.filter(l =>
            (l.status === 'site_visit_scheduled' || l.status === 'site_visit_done') &&
            l.updated_at >= weekAgo
          ).length;
          const conversions = leads.filter(l => l.status === 'won').length;
          const conversionRate = leads.length > 0
            ? ((conversions / leads.length) * 100).toFixed(1) + '%' : '0%';
          const quotationPending = leads.filter(l => l.status === 'quotation_pending').length;
          const notInterested = leads.filter(l => l.status === 'not_interested').length;

          setStats({
            followUps, activeLeads, siteVisits, conversions,
            overdueFollowUps, newLeadsThisWeek, siteVisitsThisWeek,
            conversionRate, totalLeads: leads.length, quotationPending, notInterested,
          });

          const statusCounts: Record<string, number> = {};
          leads.forEach(l => {
            statusCounts[l.status] = (statusCounts[l.status] || 0) + 1;
          });

          const chartItems: ChartDataItem[] = [
            { name: 'Attempted', value: statusCounts['attempted'] || 0, color: '#6b7280' },
            { name: 'Connected', value: statusCounts['connected'] || 0, color: '#8b5cf6' },
            { name: 'Interested', value: statusCounts['interested'] || 0, color: '#10b981' },
            { name: 'Follow-up', value: statusCounts['follow_up'] || 0, color: '#f59e0b' },
            { name: 'Mktg', value: statusCounts['marketing_assign'] || 0, color: '#ec4899' },
            { name: 'Site', value: (statusCounts['site_visit_scheduled'] || 0) + (statusCounts['site_visit_done'] || 0), color: '#06b6d4' },
            { name: 'Won', value: statusCounts['won'] || 0, color: '#22c55e' },
          ];
          setChartData(chartItems);
          setRecentLeads(leads.slice(0, 5) as RecentLead[]);
        }
      } catch (err) {
        console.log('Error loading home data:', err);
      } finally {
        setDataLoading(false);
      }
    }

    loadData();
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f2f2f2] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const firstName = userProfile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
  const roleLabel = userProfile?.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Executive';
  const initials = userProfile?.full_name
    ? userProfile.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : (user?.email?.[0] || 'U').toUpperCase();

  // Goal progress (monthly target = 30 conversions)
  const monthlyTarget = 30;
  const goalPct = Math.min(Math.round((stats.conversions / monthlyTarget) * 100), 100);
  const activeLeadPct = Math.min(Math.round((stats.activeLeads / Math.max(stats.totalLeads, 1)) * 100), 100);
  const followUpPct = Math.min(Math.round((stats.followUps / Math.max(stats.activeLeads, 1)) * 100), 100);

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f2f2f2]">
        {/* Header */}
        <div className="bg-white px-4 pt-4 pb-3 flex items-center justify-between sticky top-0 z-40 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl overflow-hidden bg-red-50 flex items-center justify-center">
              <Image
                src="/assets/images/images_4_-1778087530013.jpg"
                alt="JMC logo"
                width={36}
                height={36}
                className="object-cover w-full h-full"
                priority
              />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 leading-none">Jain Marble</p>
              <p className="text-xs text-gray-500 leading-none mt-0.5">{roleLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center relative">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {stats.overdueFollowUps > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#C41E3A] rounded-full text-white text-[9px] font-bold flex items-center justify-center">
                  {stats.overdueFollowUps > 9 ? '9+' : stats.overdueFollowUps}
                </span>
              )}
            </button>
            <div className="w-9 h-9 rounded-full bg-[#C41E3A] flex items-center justify-center">
              <span className="text-white text-xs font-bold">{initials}</span>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 space-y-5">

          {/* ── Greeting Banner ─────────────────────────────────────── */}
          <div className="rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #8B0000 0%, #C41E3A 60%, #e8294a 100%)' }}>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-red-200 text-xs font-medium mb-0.5">{greeting}</p>
                  <h2 className="text-white font-bold text-xl leading-tight">{firstName}!</h2>
                  <p className="text-red-100 text-xs mt-2 leading-relaxed">
                    {stats.followUps} follow-ups · {stats.siteVisits} site visits · {stats.quotationPending} quotes pending
                  </p>
                </div>
                <div className="flex flex-col items-center ml-4">
                  <div className="relative">
                    <CircleProgress pct={goalPct} color="#ffffff" size={52} />
                    <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">{goalPct}%</span>
                  </div>
                  <p className="text-red-200 text-[10px] mt-1 text-center">
                    Monthly
                    <br />
                    Goal
                  </p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-red-200 text-[10px]">Conversions this month</span>
                  <span className="text-white text-[10px] font-bold">{stats.conversions} / {monthlyTarget}</span>
                </div>
                <div className="h-1.5 bg-white bg-opacity-20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-700"
                    style={{ width: `${goalPct}%` }}
                  />
                </div>
              </div>
            </div>
            {/* Quick action strip */}
            <div className="bg-black bg-opacity-20 px-4 py-2.5 flex items-center gap-4">
              <Link href="/lead-management" className="flex items-center gap-1.5 text-white text-xs font-semibold">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Lead
              </Link>
              <div className="w-px h-3 bg-white bg-opacity-30" />
              <Link href="/schedule" className="flex items-center gap-1.5 text-white text-xs font-semibold">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Schedule
              </Link>
              <div className="w-px h-3 bg-white bg-opacity-30" />
              <Link href="/reports-analytics" className="flex items-center gap-1.5 text-white text-xs font-semibold">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                </svg>
                Reports
              </Link>
            </div>
          </div>

          {/* ── KPI Cards ─────────────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">Today&apos;s Overview</h3>
              <span className="text-xs text-gray-400">Live</span>
            </div>
            <div className="grid grid-cols-2 gap-3">

              {/* Follow-ups */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                    </svg>
                  </div>
                  <MiniSparkline data={sparkFollow} color="#f59e0b" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{dataLoading ? '—' : stats.followUps}</p>
                <p className="text-xs text-gray-500 mt-0.5">Follow-ups</p>
                {stats.overdueFollowUps > 0 && (
                  <div className="mt-2 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C41E3A]" />
                    <p className="text-xs text-[#C41E3A] font-semibold">{stats.overdueFollowUps} overdue</p>
                  </div>
                )}
              </div>

              {/* Active Leads */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <MiniSparkline data={sparkActive} color="#3b82f6" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{dataLoading ? '—' : stats.activeLeads}</p>
                <p className="text-xs text-gray-500 mt-0.5">Active Leads</p>
                <div className="mt-2 flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3">
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                  <p className="text-xs text-green-600 font-semibold">+{stats.newLeadsThisWeek} this week</p>
                </div>
              </div>

              {/* Site Visits */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-cyan-50 rounded-xl flex items-center justify-center">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <MiniSparkline data={sparkVisit} color="#06b6d4" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{dataLoading ? '—' : stats.siteVisits}</p>
                <p className="text-xs text-gray-500 mt-0.5">Site Visits</p>
                <p className="text-xs text-gray-400 mt-2 font-medium">{stats.siteVisitsThisWeek} this week</p>
              </div>

              {/* Conversions */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                    </svg>
                  </div>
                  <MiniSparkline data={sparkConv} color="#22c55e" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{dataLoading ? '—' : stats.conversions}</p>
                <p className="text-xs text-gray-500 mt-0.5">Conversions</p>
                <p className="text-xs text-gray-400 mt-2 font-medium">{stats.conversionRate} rate</p>
              </div>

            </div>
          </div>

          {/* ── Pipeline Health ──────────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Pipeline Health</h3>
            <div className="space-y-3">
              {/* Active vs Total */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 font-medium">Active Leads</span>
                  <span className="text-xs font-bold text-gray-900">{stats.activeLeads} <span className="text-gray-400 font-normal">/ {stats.totalLeads}</span></span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all duration-700" style={{ width: `${activeLeadPct}%` }} />
                </div>
              </div>
              {/* Follow-ups */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 font-medium">Follow-up Rate</span>
                  <span className="text-xs font-bold text-gray-900">{followUpPct}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all duration-700" style={{ width: `${followUpPct}%` }} />
                </div>
              </div>
              {/* Monthly goal */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600 font-medium">Monthly Goal</span>
                  <span className="text-xs font-bold text-gray-900">{stats.conversions} <span className="text-gray-400 font-normal">/ {monthlyTarget}</span></span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${goalPct}%`, background: goalPct >= 80 ? '#22c55e' : goalPct >= 50 ? '#f59e0b' : '#C41E3A' }}
                  />
                </div>
              </div>
            </div>
            {/* Status pills */}
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                {stats.quotationPending} Quotes Pending
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">
                {stats.overdueFollowUps} Overdue
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 font-medium">
                {stats.notInterested} Not Interested
              </span>
            </div>
          </div>

          {/* ── Quick Actions ────────────────────────────────────────── */}
          <div>
            <h3 className="text-base font-bold text-gray-900 mb-3">Quick Actions</h3>
            <div className="grid grid-cols-4 gap-3">
              <Link href="/lead-management" className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" />
                    <line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 font-medium text-center">Add Lead</span>
              </Link>
              <Link href="/site-visit" className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-cyan-50 rounded-2xl flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 font-medium text-center">Site Visit</span>
              </Link>
              <Link href="/reports-analytics" className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 font-medium text-center">Reports</span>
              </Link>
              <Link href="/user-management" className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <span className="text-xs text-gray-600 font-medium text-center">Team</span>
              </Link>
            </div>
          </div>

          {/* ── Lead Stage Distribution Chart ────────────────────────── */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">Lead Stage Distribution</h3>
              <span className="text-xs font-semibold text-[#C41E3A] bg-red-50 px-2.5 py-1 rounded-full">Live</span>
            </div>
            {dataLoading ? (
              <div className="h-40 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={28}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-3">
                  {chartData.map((item) => (
                    <div key={item.name} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-gray-500">{item.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Alerts & Nudges ──────────────────────────────────────── */}
          {(stats.overdueFollowUps > 0 || stats.quotationPending > 0) && (
            <div className="space-y-2">
              <h3 className="text-base font-bold text-gray-900">Alerts</h3>
              {stats.overdueFollowUps > 0 && (
                <div className="bg-red-50 border border-red-100 rounded-2xl p-3.5 flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2.5">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-red-800">{stats.overdueFollowUps} overdue follow-ups</p>
                    <p className="text-xs text-red-600 mt-0.5">Leads not actioned today — needs attention</p>
                  </div>
                  <Link href="/lead-management" className="text-xs font-bold text-[#C41E3A] flex-shrink-0">View →</Link>
                </div>
              )}
              {stats.quotationPending > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3.5 flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-bold text-blue-800">{stats.quotationPending} quotations pending</p>
                    <p className="text-xs text-blue-600 mt-0.5">Awaiting approval or client response</p>
                  </div>
                  <Link href="/lead-management" className="text-xs font-bold text-blue-600 flex-shrink-0">View →</Link>
                </div>
              )}
            </div>
          )}

          {/* ── Recent Leads ─────────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">Recent Leads</h3>
              <Link href="/lead-management" className="text-sm font-semibold text-[#C41E3A]">View All</Link>
            </div>
            <div className="space-y-2.5">
              {dataLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-4 shadow-sm animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/3" />
                  </div>
                ))
              ) : recentLeads.map((lead, idx) => (
                <Link key={lead.id} href={`/lead-management`}>
                  <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm text-white"
                      style={{ background: ['#C41E3A', '#3b82f6', '#8b5cf6', '#f59e0b', '#22c55e'][idx % 5] }}>
                      {lead.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-bold text-gray-900 truncate">{lead.name}</h4>
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-lg flex-shrink-0"
                          style={getStatusBadgeStyle(lead.status)}
                        >
                          {statusLabels[lead.status] || lead.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-500 truncate">{lead.company || lead.phone}</p>
                        {lead.area_sqft > 0 && (
                          <span className="text-xs text-gray-400 flex-shrink-0">· {lead.area_sqft?.toLocaleString()} sqft</span>
                        )}
                        <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{getTimeAgo(lead.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* ── Summary Footer Strip ─────────────────────────────────── */}
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Lead Breakdown</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xl font-bold text-gray-900">{dataLoading ? '—' : stats.totalLeads}</p>
                <p className="text-xs text-gray-500 mt-0.5">Total Leads</p>
              </div>
              <div className="border-x border-gray-100">
                <p className="text-xl font-bold text-green-600">{dataLoading ? '—' : stats.conversions}</p>
                <p className="text-xs text-gray-500 mt-0.5">Won</p>
              </div>
              <div>
                <p className="text-xl font-bold text-gray-400">{dataLoading ? '—' : stats.notInterested}</p>
                <p className="text-xs text-gray-500 mt-0.5">Not Interested</p>
              </div>
            </div>
            <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden flex">
              {stats.totalLeads > 0 && (
                <>
                  <div className="h-full bg-green-500 transition-all" style={{ width: `${(stats.conversions / stats.totalLeads) * 100}%` }} />
                  <div className="h-full bg-blue-400 transition-all" style={{ width: `${(stats.activeLeads / stats.totalLeads) * 100}%` }} />
                  <div className="h-full bg-gray-300 transition-all" style={{ width: `${(stats.notInterested / stats.totalLeads) * 100}%` }} />
                </>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />Won</span>
              <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />Active</span>
              <span className="flex items-center gap-1 text-[10px] text-gray-500"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />Not Interested</span>
            </div>
          </div>

        </div>

        {/* FAB */}
        <Link
          href="/lead-management"
          className="fixed bottom-20 right-4 w-14 h-14 bg-[#C41E3A] rounded-2xl flex items-center justify-center shadow-lg z-40"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </Link>

      </div>
    </AppLayout>
  );
}
